import { esAparcada, ETAPAS_COMERCIAL, ETAPAS_QUE_APARCAN } from "./etapas";
import { aFechaISO, inicioDelDia, sumarDias } from "./fechas";
import type {
  AjustesNevora,
  Canal,
  Etapa,
  FechaHoraISO,
  FechaISO,
  Negocio,
  NivelInteres,
  NotaNueva,
  Resultado,
} from "./tipos";

/** Datos que acompañan a un resultado (los pide la hoja). */
export type DatosResultado = {
  /** Cuándo se hizo el contacto. Por defecto, ahora. */
  fecha?: FechaHoraISO;
  texto?: string;
  reaccion?: string;
  objecion?: string;
  /** quiere_reunion */
  fechaReunion?: FechaHoraISO;
  /** ni_si_ni_no, lo_pensara y anotar */
  fechaSiguientePaso?: FechaISO;
  /** 33: hora opcional del siguiente paso, «HH:MM». */
  horaSiguientePaso?: string | null;
  siguientePaso?: string;
  /** mas_adelante */
  fechaVuelta?: FechaISO;
  /** no_interesa */
  noLlamar?: boolean;
  /** Nivel de interés (28d): obligatorio en «Ni sí ni no» de llamada o visita. */
  interes?: NivelInteres | null;
};

export type EfectoResultado = {
  /** Columnas que cambian (del proyecto, y del negocio: ultimo_contacto, no_llamar). */
  cambios: Partial<Negocio>;
  /** Nota que se añade al historial. */
  nota: NotaNueva;
  /** true si el proyecto ha quedado aparcado por acumular "No contesta". */
  aparcadaPorIntentos: boolean;
};

/** Error de validación con clave i18n (`nevora.resultado.errores.*`). */
export class ErrorResultado extends Error {
  constructor(public readonly clave: string) {
    super(clave);
    this.name = "ErrorResultado";
  }
}

const limpiar = (s?: string) => {
  const t = s?.trim();
  return t ? t : undefined;
};

type ProyectoParaResultado = Pick<
  Negocio,
  "embudo" | "etapa" | "etapa_antes_de_aparcar" | "intentos" | "no_llamar"
> &
  Partial<Pick<Negocio, "no_shows" | "siguiente_paso" | "hora_siguiente_paso">>;

/** 46: el siguiente paso que programa «No contesta» en un lead nuevo. */
export const PASO_REINTENTO = "Reintentar llamada";

/**
 * 46 (auditoría 30/10/2026): cuándo volver a llamar tras un «No contesta», en la otra franja:
 * un día laborable antes de las 13:00 → ese día a las 16:30; si no → el siguiente laborable a
 * las 10:00. Igual que `nevora_siguiente_intento` en la base (hora de Madrid).
 */
export const siguienteIntento = (
  desde: Date,
): { fecha: FechaISO; hora: string } => {
  const laborable = (d: Date) => d.getDay() >= 1 && d.getDay() <= 5;
  if (laborable(desde) && desde.getHours() < 13) {
    return { fecha: aFechaISO(desde), hora: "16:30:00" };
  }
  let dia = sumarDias(inicioDelDia(desde), 1);
  while (!laborable(dia)) dia = sumarDias(dia, 1);
  return { fecha: aFechaISO(dia), hora: "10:00:00" };
};

/** 36: motivos que pone la base con estos resultados (se ven en la nota). */
export const MOTIVO_NO_SE_PRESENTO = "No se presentó";
export const MOTIVO_NO_QUIERE_CONTACTO = "No quiere que le contactemos";
/** 36: al tercer «No se presentó», «No interesado». */
export const MAX_NO_SHOWS = 3;

/** ¿El nivel de interés es obligatorio? En «Ni sí ni no» al hablar con él (llamada o visita). */
export const pideInteres = (resultado: Resultado | null, canal: Canal) =>
  resultado === "ni_si_ni_no" && (canal === "llamada" || canal === "visita");

const enEtapaDelComercial = (p: ProyectoParaResultado) =>
  p.embudo === "ventas" &&
  (ETAPAS_COMERCIAL.includes(p.etapa) ||
    (esAparcada(p.etapa) &&
      ETAPAS_COMERCIAL.includes(p.etapa_antes_de_aparcar ?? "nuevo")));

/**
 * Regla de los botones (Robert, 02/10/2026). Función pura, espejo EXACTO de la función SQL
 * `registrar_resultado` (S8), que es la que guarda: aquí se valida antes de llamarla, para
 * avisar en castellano, y se prueba la regla.
 *
 * Contacto (He llamado / He visitado / He escrito), en las etapas del comercial:
 * | quiere_reunion | → Consultoría agendada, con fecha y hora                          |
 * | interesado     | → En conversación, con fecha del siguiente paso y la marca       |
 * |                | «Interesado» (28c); cualquier otro resultado la quita             |
 * | ni_si_ni_no    | → En conversación, con fecha del siguiente contacto               |
 * | no_contesta    | intentos + 1; al llegar a los de Ajustes en Lead nuevo o En        |
 * |                | conversación → No contesta                                       |
 * | no_interesa    | → No interesado, con motivo; opcionalmente «no volver a contactar» |
 * | mas_adelante   | → Largo plazo, con fecha de vuelta                                 |
 *
 * Resultado de la presentación (Presentación agendada, Se lo tiene que pensar):
 * | aceptada    | → Aceptada · contrato y factura                    |
 * | lo_pensara  | → Se lo tiene que pensar, con la fecha de respuesta |
 * | repropuesta | → Preparando propuesta, con qué quiere cambiar      |
 * | no_interesa | → No interesado, con motivo                         |
 *
 * `resultado` null = anotar el contacto y su siguiente paso sin mover la tarjeta (de
 * Preparando propuesta en adelante y en Clientes). Cualquier resultado distinto de "No
 * contesta" es una conversación: intentos a 0.
 */
export const aplicarResultado = (
  proyecto: ProyectoParaResultado,
  canal: Canal,
  resultado: Resultado | null,
  datos: DatosResultado,
  ajustes: Pick<AjustesNevora, "maxIntentos">,
  ahora: Date = new Date(),
): EfectoResultado => {
  if (
    proyecto.no_llamar &&
    resultado !== "no_interesa" &&
    resultado !== "no_quiere_contacto"
  ) {
    throw new ErrorResultado("nevora.resultado.errores.no_llamar");
  }
  let objecion = limpiar(datos.objecion);

  const fecha = datos.fecha ?? (ahora.toISOString() as FechaHoraISO);
  const cambios: Partial<Negocio> = { ultimo_contacto: fecha };
  let etapaNueva: Etapa = proyecto.etapa;
  let aparcadaPorIntentos = false;

  if (resultado === null) {
    const paso = limpiar(datos.siguientePaso);
    if (paso && !datos.fechaSiguientePaso) {
      throw new ErrorResultado(
        "nevora.resultado.errores.falta_fecha_siguiente_paso",
      );
    }
    if (datos.fechaSiguientePaso) {
      cambios.siguiente_paso = paso ?? "Volver a contactar";
      cambios.fecha_siguiente_paso = datos.fechaSiguientePaso;
    }
  } else if (
    resultado === "quiere_reunion" ||
    resultado === "interesado" ||
    resultado === "ni_si_ni_no" ||
    resultado === "no_contesta" ||
    resultado === "mas_adelante"
  ) {
    if (!enEtapaDelComercial(proyecto)) {
      throw new ErrorResultado("nevora.resultado.errores.solo_anotar");
    }
    switch (resultado) {
      case "quiere_reunion": {
        if (!datos.fechaReunion) {
          throw new ErrorResultado(
            "nevora.resultado.errores.falta_fecha_reunion",
          );
        }
        etapaNueva = "consultoria_agendada";
        cambios.fecha_reunion = datos.fechaReunion;
        cambios.siguiente_paso = null;
        cambios.fecha_siguiente_paso = null;
        cambios.fecha_vuelta = null;
        cambios.intentos = 0;
        break;
      }
      case "interesado": {
        if (!datos.fechaSiguientePaso) {
          throw new ErrorResultado(
            "nevora.resultado.errores.falta_fecha_siguiente_paso",
          );
        }
        etapaNueva = "en_conversacion";
        cambios.fecha_siguiente_paso = datos.fechaSiguientePaso;
        cambios.siguiente_paso =
          limpiar(datos.siguientePaso) ??
          "Confirmar la fecha de la consultoría";
        cambios.fecha_vuelta = null;
        cambios.intentos = 0;
        break;
      }
      case "ni_si_ni_no": {
        if (!datos.fechaSiguientePaso) {
          throw new ErrorResultado(
            "nevora.resultado.errores.falta_fecha_siguiente_paso",
          );
        }
        // 28d: solo si la base ya tiene el nivel (la ficha trae la columna).
        if (
          "interes" in proyecto &&
          pideInteres(resultado, canal) &&
          !datos.interes
        ) {
          throw new ErrorResultado("nevora.resultado.errores.falta_interes");
        }
        etapaNueva = "en_conversacion";
        cambios.fecha_siguiente_paso = datos.fechaSiguientePaso;
        cambios.siguiente_paso =
          limpiar(datos.siguientePaso) ?? "Volver a contactar";
        cambios.fecha_vuelta = null;
        cambios.intentos = 0;
        break;
      }
      case "no_contesta": {
        const intentos = proyecto.intentos + 1;
        cambios.intentos = intentos;
        if (
          ETAPAS_QUE_APARCAN.includes(proyecto.etapa) &&
          intentos >= ajustes.maxIntentos
        ) {
          etapaNueva = "no_contesta";
          aparcadaPorIntentos = true;
          // 46: aparcada, sin el reintento que quedaba programado.
          if (proyecto.siguiente_paso === PASO_REINTENTO) {
            cambios.siguiente_paso = null;
            cambios.fecha_siguiente_paso = null;
          }
        } else if (proyecto.etapa === "nuevo") {
          // 46: el siguiente intento, en la otra franja.
          const r = siguienteIntento(new Date(fecha));
          cambios.siguiente_paso = PASO_REINTENTO;
          cambios.fecha_siguiente_paso = r.fecha;
          if ("hora_siguiente_paso" in proyecto)
            cambios.hora_siguiente_paso = r.hora;
        }
        break;
      }
      case "mas_adelante": {
        if (!datos.fechaVuelta) {
          throw new ErrorResultado(
            "nevora.resultado.errores.falta_fecha_vuelta",
          );
        }
        // 47 (Robert, 31/10/2026): también con motivo, para saber por qué se aparca. Lo exige
        // la app, no la base (como el nivel de la 28e: la versión anterior sigue funcionando).
        if (!limpiar(datos.objecion)) {
          throw new ErrorResultado("nevora.resultado.errores.falta_motivo");
        }
        etapaNueva = "largo_plazo";
        cambios.fecha_vuelta = datos.fechaVuelta;
        cambios.siguiente_paso = null;
        cambios.fecha_siguiente_paso = null;
        cambios.intentos = 0;
        break;
      }
    }
  } else if (resultado === "no_se_presento") {
    // 36: desde la consultoría agendada (o desde No show); la ficha sigue siendo del setter.
    if (
      proyecto.embudo !== "ventas" ||
      (proyecto.etapa !== "consultoria_agendada" &&
        proyecto.etapa !== "no_show")
    ) {
      throw new ErrorResultado("nevora.resultado.errores.sin_consultoria");
    }
    const noShows = (proyecto.no_shows ?? 0) + 1;
    cambios.no_shows = noShows;
    cambios.fecha_vuelta = null;
    cambios.intentos = 0;
    if (noShows >= MAX_NO_SHOWS) {
      etapaNueva = "no_interesado";
      objecion = objecion ?? MOTIVO_NO_SE_PRESENTO;
      cambios.siguiente_paso = null;
      cambios.fecha_siguiente_paso = null;
    } else {
      etapaNueva = "no_show";
      cambios.siguiente_paso =
        limpiar(datos.siguientePaso) ?? "Llamar para reagendar la consultoría";
      cambios.fecha_siguiente_paso =
        datos.fechaSiguientePaso ?? aFechaISO(ahora);
    }
  } else if (resultado === "no_quiere_contacto") {
    // 36: en los tres botones: «no llamar», «No interesado» y la lista de no contactar.
    if (proyecto.embudo !== "ventas") {
      throw new ErrorResultado("nevora.resultado.errores.solo_anotar");
    }
    etapaNueva = "no_interesado";
    objecion = objecion ?? MOTIVO_NO_QUIERE_CONTACTO;
    if (!proyecto.no_llamar) cambios.no_llamar = true;
    cambios.siguiente_paso = null;
    cambios.fecha_siguiente_paso = null;
    cambios.fecha_vuelta = null;
    cambios.intentos = 0;
  } else if (resultado === "no_interesa") {
    if (proyecto.embudo !== "ventas") {
      throw new ErrorResultado("nevora.resultado.errores.solo_anotar");
    }
    // Motivo obligatorio (02/10/2026): se guarda como objeción de la nota y el Inicio
    // cuenta cuál se repite.
    if (!limpiar(datos.objecion)) {
      throw new ErrorResultado("nevora.resultado.errores.falta_motivo");
    }
    etapaNueva = "no_interesado";
    if (datos.noLlamar === true && !proyecto.no_llamar) {
      cambios.no_llamar = true;
    }
    cambios.siguiente_paso = null;
    cambios.fecha_siguiente_paso = null;
    cambios.fecha_vuelta = null;
    cambios.intentos = 0;
  } else {
    if (
      proyecto.etapa !== "presentacion_agendada" &&
      proyecto.etapa !== "pensandolo"
    ) {
      throw new ErrorResultado("nevora.resultado.errores.no_presentacion");
    }
    switch (resultado) {
      case "aceptada":
        etapaNueva = "aceptada";
        cambios.siguiente_paso = null;
        cambios.fecha_siguiente_paso = null;
        break;
      case "lo_pensara":
        if (!datos.fechaSiguientePaso) {
          throw new ErrorResultado(
            "nevora.resultado.errores.falta_fecha_respuesta",
          );
        }
        etapaNueva = "pensandolo";
        cambios.siguiente_paso =
          limpiar(datos.siguientePaso) ?? "Esperar su respuesta";
        cambios.fecha_siguiente_paso = datos.fechaSiguientePaso;
        break;
      case "repropuesta":
        if (!limpiar(datos.texto)) {
          throw new ErrorResultado("nevora.resultado.errores.falta_cambios");
        }
        etapaNueva = "preparando_propuesta";
        cambios.siguiente_paso = null;
        cambios.fecha_siguiente_paso = null;
        break;
    }
  }

  // Marca «Interesado» (28c): la pone ese resultado y la quita cualquier otro; anotar sin
  // resultado no la toca (los cambios de etapa la quitan en la base).
  if (resultado === "interesado") {
    cambios.interesado_at = ahora.toISOString() as FechaHoraISO;
  } else if (resultado !== null && "interesado_at" in proyecto) {
    // Solo si la base ya tiene la columna: sin la 28c, la ficha no la trae y no se añade
    // (la app la usa para saber si ofrecer «Interesado»).
    cambios.interesado_at = null;
  }

  // Hora del siguiente paso (33): la que llegue con la fecha nueva; sin siguiente paso, ninguna.
  if ("hora_siguiente_paso" in proyecto) {
    if (cambios.fecha_siguiente_paso === null)
      cambios.hora_siguiente_paso = null;
    else if (datos.fechaSiguientePaso && cambios.fecha_siguiente_paso)
      cambios.hora_siguiente_paso = datos.horaSiguientePaso || null;
  }

  // Nivel de interés (28d): «Interesado» = caliente; en lo demás, el que se elija (si no, el
  // que tenía). Solo si la base ya lo tiene.
  if ("interes" in proyecto) {
    if (resultado === "interesado") cambios.interes = "caliente";
    else if (datos.interes) cambios.interes = datos.interes;
  }

  if (etapaNueva !== proyecto.etapa) {
    cambios.etapa = etapaNueva;
  }

  const nota: NotaNueva = {
    fecha,
    canal,
    resultado,
    texto: limpiar(datos.texto) ?? null,
    reaccion: limpiar(datos.reaccion) ?? null,
    objecion: objecion ?? null,
    fase_anterior: proyecto.etapa,
    fase_nueva: etapaNueva,
  };

  return { cambios, nota, aparcadaPorIntentos };
};

/** Datos extra que pide cada resultado en la hoja. `null` = anotar (paso opcional). */
export type DatoDeResultado =
  | "fechaReunion"
  | "fechaSiguientePaso"
  | "fechaVuelta"
  | "motivo"
  | "noLlamar"
  | "fechaRespuesta"
  | "cambios"
  | "pasoOpcional";

export const datosQuePide = (
  resultado: Resultado | null,
): DatoDeResultado[] => {
  switch (resultado) {
    case null:
      return ["pasoOpcional"];
    case "quiere_reunion":
      return ["fechaReunion"];
    case "interesado":
    case "ni_si_ni_no":
      return ["fechaSiguientePaso"];
    case "mas_adelante":
      return ["fechaVuelta", "motivo"];
    // 36: «No se presentó» propone llamar para reagendar (hoy, si no se pone otra fecha).
    case "no_se_presento":
      return ["pasoOpcional"];
    case "no_interesa":
      return ["motivo", "noLlamar"];
    case "lo_pensara":
      return ["fechaRespuesta"];
    case "repropuesta":
      return ["cambios"];
    default:
      return [];
  }
};

/** Parámetro p_datos de la función SQL registrar_resultado. */
export const datosParaRpc = (datos: DatosResultado) => ({
  fecha: datos.fecha ?? null,
  texto: datos.texto ?? null,
  reaccion: datos.reaccion ?? null,
  objecion: datos.objecion ?? null,
  siguiente_paso: datos.siguientePaso ?? null,
  fecha_siguiente_paso: datos.fechaSiguientePaso ?? null,
  fecha_reunion: datos.fechaReunion ?? null,
  fecha_vuelta: datos.fechaVuelta ?? null,
  no_llamar: datos.noLlamar === true,
  interes: datos.interes ?? null,
  hora_siguiente_paso: datos.horaSiguientePaso || null,
});
